import type { ChangeWorkGroup } from '../../types/changes';
import type { WorkflowCustomField } from '../../types';
import {
  hydrateIncidentCustomFieldValues,
  incidentFileFieldIds,
  serializeIncidentCustomFields,
  type IncidentCustomFieldFiles,
  type IncidentCustomFieldValues,
} from './incidentTicketFields';

export type IncidentWorkflowOption = { id: number; name: string; group_ids: number[] };

export interface IncidentSendDraft {
  workflow_id: number | '';
  incidents_group_id?: number;
  title: string;
  comments: string;
  urgency: string;
  files: File[];
  customFields: WorkflowCustomField[];
  customFieldValues: IncidentCustomFieldValues;
  customFieldFiles: IncidentCustomFieldFiles;
}

export function incidentQuickTitle(group: ChangeWorkGroup, workflowName?: string): string {
  const custom = (group.incident_title_default || '').trim();
  if (custom) return custom;
  if (workflowName && group.name) return `${workflowName} — ${group.name}`;
  return group.name || 'Chamado no Incidente';
}

export function incidentCustomFieldDefaultsForWorkflow(
  group: ChangeWorkGroup,
  workflowId: number | '',
): IncidentCustomFieldValues {
  if (!workflowId || Number(workflowId) !== Number(group.incidents_workflow_id || 0)) {
    return {};
  }
  return hydrateIncidentCustomFieldValues(group.incident_custom_field_defaults);
}

export type EmptyIncidentDraftOptions = {
  /** Igual Novo Ticket: usuário escolhe o fluxo antes dos campos do catálogo. */
  blankWorkflow?: boolean;
};

export function emptyIncidentDraft(
  group: ChangeWorkGroup,
  options?: EmptyIncidentDraftOptions,
): IncidentSendDraft {
  const blankWorkflow = Boolean(options?.blankWorkflow);
  const workflowId = blankWorkflow ? '' : group.incidents_workflow_id || '';
  return {
    workflow_id: workflowId,
    incidents_group_id: blankWorkflow ? undefined : group.incidents_group_id || undefined,
    title: blankWorkflow ? '' : incidentQuickTitle(group),
    comments: blankWorkflow ? '' : (group.incident_description_default || '').trim(),
    urgency: blankWorkflow ? '' : group.incident_urgency_default || '',
    files: [],
    customFields: [],
    customFieldValues: incidentCustomFieldDefaultsForWorkflow(group, workflowId),
    customFieldFiles: {},
  };
}

export function incidentAllowsAttachments(group: ChangeWorkGroup): boolean {
  return group.incident_attachment_allowed !== false;
}

export function workflowsForIncidentGroup(
  group: ChangeWorkGroup,
  workflows: IncidentWorkflowOption[],
): IncidentWorkflowOption[] {
  const queueId = group.incidents_group_id;
  return workflows.filter((flow) => !queueId || flow.group_ids.includes(queueId));
}

function normalizeIncidentLabel(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

export function matchIncidentQueue(
  groupName: string,
  queues: Array<{ id: number; name: string }>,
): number | undefined {
  const needle = normalizeIncidentLabel(groupName || '');
  if (!needle) return undefined;
  const exact = queues.find((queue) => normalizeIncidentLabel(queue.name) === needle);
  if (exact) return exact.id;
  const token = needle.split(' ')[0];
  if (token.length < 3) return undefined;
  const hits = queues.filter((queue) => {
    const hay = normalizeIncidentLabel(queue.name);
    return hay === token || hay.startsWith(`${token} `) || hay.includes(` ${token} `);
  });
  return hits.length === 1 ? hits[0].id : undefined;
}

export function pickFirstContactGroup(
  groups: Array<{ id: number; name: string }>,
  savedId?: number | null,
  workGroupName?: string | null,
): number | undefined {
  if (savedId && groups.some((item) => item.id === savedId)) return savedId;
  const matched = matchIncidentQueue(workGroupName || '', groups);
  if (matched) return matched;
  return groups.length === 1 ? groups[0].id : undefined;
}

/**
 * Mesma fonte do Novo Ticket (Incidentes): first-contact-groups.
 * Se a API vier vazia, usa só filas reais do módulo Incidentes ligadas ao fluxo (incident-targets).
 */
export function forwardQueuesForWorkflow(
  workflowId: number | '',
  workflows: IncidentWorkflowOption[],
  incidentQueues: Array<{ id: number; name: string }>,
  apiGroups: Array<{ id: number; name: string }>,
): Array<{ id: number; name: string }> {
  if (apiGroups.length) return apiGroups;
  if (!workflowId) return [];
  const flow = workflows.find((item) => item.id === Number(workflowId));
  if (!flow?.group_ids?.length) return [];
  const allowed = new Set(flow.group_ids);
  return incidentQueues.filter((queue) => allowed.has(queue.id));
}

export function defaultIncidentWorkflowId(
  queueId: number | undefined,
  workflows: IncidentWorkflowOption[],
  savedWorkflowId?: number | null,
): number | undefined {
  const flows = workflows.filter((flow) => !queueId || flow.group_ids.includes(queueId));
  if (savedWorkflowId && flows.some((flow) => flow.id === savedWorkflowId)) {
    return savedWorkflowId;
  }
  return flows.length === 1 ? flows[0].id : undefined;
}

export function incidentCustomFieldsPayload(draft: IncidentSendDraft) {
  return {
    incident_custom_fields: serializeIncidentCustomFields(draft.customFields, draft.customFieldValues),
    incident_file_field_ids: incidentFileFieldIds(
      draft.customFields,
      draft.customFieldFiles,
      draft.customFieldValues,
    ),
  };
}
